import type { AccountEnrollmentListItemDto } from '@repo/types';
import { ACCOUNT_KIND_OPTIONS_ORDER, PAYMENT_STATE_LABELS, PaymentSortBy, ROLE_LABELS } from '@repo/types';
import { useQueryClient } from '@tanstack/react-query';
import { ACCOUNT_KIND, ACCOUNT_STATE, ENROLLMENT_STATE, PAYMENT_STATE } from '@theailab-dev/parchment';
import type { AccountKind, AccountState, EnrollmentState, PaymentMethod, PaymentState } from '@theailab-dev/parchment';
import React, { useState } from 'react';
import { HiRefresh, HiPencil, HiTrash } from 'react-icons/hi';
import { useParams, useNavigate } from 'react-router-dom';
import { getAccountEnrollments, resetAccountPassword, changeAccountKind, deleteAccount } from '../../api/accountApi';
import { getPayments } from '../../api/paymentApi';
import { DetailPageLayout } from '../../components/Layout/DetailPageLayout';
import { Button, Card, Badge, InfoItem } from '../../components/ui';
import type { BadgeVariant } from '../../components/ui';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { useAccountDetail } from '../../hooks/useAccountDetail';
import { usePayments } from '../../hooks/usePayments';
import { useAuthStore } from '../../store/authStore';
import { canChangeToKind, canModifyAccount } from '../../utils/auth';
import { formatDateTimeKoreanLocale } from '../../utils/dateTime';

/**
 * UserDetail 컴포넌트 - 사용자 상세 정보 페이지
 */
const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accountId = userId ? parseInt(userId, 10) : 0;
  const { user: currentUser } = useAuthStore();

  const { data: account, isLoading, isError, error, refetch } = useAccountDetail(accountId);

  // 수강 정보 페이지네이션 관리
  const [additionalEnrollments, setAdditionalEnrollments] = useState<AccountEnrollmentListItemDto[]>([]);
  const [currentPage, setCurrentPage] = useState(1); // 현재 로드된 페이지
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 결제 내역 페이지네이션 관리
  const [additionalPayments, setAdditionalPayments] = useState<
    Array<{
      id: number;
      orderSheetId: number;
      buyerEmail: string;
      buyerName: string;
      state: PaymentState;
      method: PaymentMethod;
      amount: number;
      createdAt: Date;
    }>
  >([]);
  const [currentPaymentPage, setCurrentPaymentPage] = useState(1);
  const [isLoadingMorePayments, setIsLoadingMorePayments] = useState(false);

  // 결제 내역 조회
  const { data: paymentsData } = usePayments({
    accountId: accountId > 0 ? accountId : undefined,
    page: 1,
    limit: 5,
    sortBy: PaymentSortBy.CREATED_AT,
    sortOrder: 'DESC',
  });

  // 비밀번호 초기화 관련 상태
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  // 유형 변경 관련 상태
  const [isChangingKind, setIsChangingKind] = useState(false);
  const [showChangeKindModal, setShowChangeKindModal] = useState(false);
  const [selectedKind, setSelectedKind] = useState<AccountKind>(ACCOUNT_KIND.USER);

  // 탈퇴 처리 관련 상태
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  /**
   * account 데이터가 변경되면 selectedKind 동기화
   */
  React.useEffect(() => {
    if (account?.kind) {
      setSelectedKind(account.kind);
    }
  }, [account?.kind]);

  /**
   * 비밀번호 초기화 확인 팝업 표시
   */
  const handleResetPasswordClick = () => {
    setShowResetPasswordModal(true);
  };

  /**
   * 유형 변경 모달 열기
   */
  const handleChangeKindClick = () => {
    if (account?.kind) {
      setSelectedKind(account.kind);
    }
    setShowChangeKindModal(true);
  };

  /**
   * 유형 변경 실행
   */
  const handleConfirmChangeKind = async () => {
    if (!account || !currentUser) {
      setShowChangeKindModal(false);
      return;
    }

    // 권한 검증: 현재 사용자가 이 계정을 수정할 수 있는지 확인
    if (!canModifyAccount(currentUser.kind, account.kind)) {
      let errorMessage = '이 계정의 권한을 변경할 수 없습니다.';
      if (currentUser.kind === ACCOUNT_KIND.OFFICER) {
        errorMessage = 'OFFICER는 읽기 전용 권한입니다. 계정 권한을 변경할 수 없습니다.';
      } else if (account.kind === ACCOUNT_KIND.ADMIN) {
        errorMessage = 'ADMIN 계정의 권한은 변경할 수 없습니다.';
      }
      setShowResultModal({
        type: 'error',
        message: errorMessage,
      });
      setShowChangeKindModal(false);
      return;
    }

    // 권한 검증: 선택한 권한으로 변경할 수 있는지 확인
    if (!canChangeToKind(currentUser.kind, selectedKind)) {
      let errorMessage = '선택한 권한으로 변경할 수 없습니다.';
      if (currentUser.kind === ACCOUNT_KIND.OFFICER) {
        errorMessage = 'OFFICER는 읽기 전용 권한입니다. 계정 권한을 변경할 수 없습니다.';
      } else if (selectedKind === ACCOUNT_KIND.ADMIN) {
        errorMessage = 'ADMIN 권한으로는 변경할 수 없습니다.';
      }
      setShowResultModal({
        type: 'error',
        message: errorMessage,
      });
      setShowChangeKindModal(false);
      return;
    }

    if (selectedKind === account.kind) {
      setShowResultModal({
        type: 'info',
        message: '변경된 내용이 없습니다.',
      });
      setShowChangeKindModal(false);
      return;
    }

    try {
      setIsChangingKind(true);
      await changeAccountKind(accountId, selectedKind);

      setShowResultModal({
        type: 'success',
        message: `사용자 유형이 ${ROLE_LABELS[selectedKind] ?? selectedKind}로 변경되었습니다.`,
      });

      await queryClient.invalidateQueries({
        queryKey: ['account', accountId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['accounts'],
      });

      setShowChangeKindModal(false);
    } catch (error) {
      console.error('유형 변경 실패:', error);

      // 에러 메시지 추출
      let errorMessage = '알 수 없는 오류가 발생했습니다.';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: {
            status?: number;
            data?: { message?: string; error?: string };
          };
        };

        // 403 Forbidden: 권한 없음
        if (axiosError.response?.status === 403) {
          errorMessage =
            axiosError.response.data?.message || axiosError.response.data?.error || '이 작업을 수행할 권한이 없습니다.';
        } else if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        }
      }

      setShowResultModal({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setIsChangingKind(false);
    }
  };

  /**
   * 비밀번호 초기화 실행
   */
  const handleConfirmResetPassword = async () => {
    try {
      setIsResettingPassword(true);
      await resetAccountPassword(accountId);

      setShowResetPasswordModal(false);

      setShowResultModal({
        type: 'success',
        message: `새로운 임시 비밀번호가 ${account?.email || '사용자의 이메일'}로 전송되었습니다.`,
      });
    } catch (error) {
      console.error('비밀번호 초기화 실패:', error);
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';

      setShowResetPasswordModal(false);

      setShowResultModal({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  /**
   * 탈퇴 처리 확인 팝업 표시
   */
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  /**
   * 탈퇴 처리 실행
   */
  const handleConfirmDelete = async () => {
    if (!account) return;

    try {
      setIsDeleting(true);
      await deleteAccount(accountId);

      setShowDeleteModal(false);

      setShowResultModal({
        type: 'success',
        message: '사용자가 성공적으로 탈퇴 처리되었습니다.',
      });

      // 데이터 갱신
      await queryClient.invalidateQueries({
        queryKey: ['account', accountId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['accounts'],
      });
    } catch (error) {
      console.error('탈퇴 처리 실패:', error);
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';

      setShowDeleteModal(false);

      setShowResultModal({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * 결제 내역 더보기
   */
  const handleLoadMorePayments = async () => {
    if (!paymentsData?.meta || isLoadingMorePayments || accountId <= 0) return;

    const nextPage = currentPaymentPage + 1;

    try {
      setIsLoadingMorePayments(true);
      const response = await getPayments({
        accountId,
        page: nextPage,
        limit: 5,
        sortBy: PaymentSortBy.CREATED_AT,
        sortOrder: 'DESC',
      });

      const existingIds = new Set([
        ...(paymentsData.data?.map((p) => p.id) || []),
        ...additionalPayments.map((p) => p.id),
      ]);

      const newPayments = response.data.filter((payment) => !existingIds.has(payment.id));

      setAdditionalPayments((prev) => [...prev, ...newPayments]);
      setCurrentPaymentPage(nextPage);
    } catch (error) {
      console.error('결제 내역 더보기 실패:', error);
    } finally {
      setIsLoadingMorePayments(false);
    }
  };

  /**
   * 수강 정보 더보기
   */
  const handleLoadMoreEnrollments = async () => {
    if (!account?.enrollmentMeta || isLoadingMore) return;

    const nextPage = currentPage + 1;

    try {
      setIsLoadingMore(true);
      const response = await getAccountEnrollments(accountId, nextPage, 5);

      const existingIds = new Set([...account.enrollments.map((e) => e.id), ...additionalEnrollments.map((e) => e.id)]);

      const newEnrollments = response.data.filter((enrollment) => !existingIds.has(enrollment.id));

      setAdditionalEnrollments((prev) => [...prev, ...newEnrollments]);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error('❌ 수강 목록 추가 로드 실패:', error);
      alert('수강 목록을 불러오는 데 실패했습니다.');
    } finally {
      setIsLoadingMore(false);
    }
  };

  const getKindBadgeVariant = (kind: AccountKind): BadgeVariant => {
    switch (kind) {
      case ACCOUNT_KIND.USER:
        return 'neutral';
      case ACCOUNT_KIND.PARTNER:
        return 'cyan';
      case ACCOUNT_KIND.INSTRUCTOR:
        return 'teal';
      case ACCOUNT_KIND.OFFICER:
        return 'orange';
      case ACCOUNT_KIND.OPERATOR:
        return 'primary';
      case ACCOUNT_KIND.ADMIN:
        return 'purple';
      case ACCOUNT_KIND.SYSTEM:
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const getStateBadgeVariant = (state: AccountState) => {
    switch (state) {
      case ACCOUNT_STATE.NORMAL:
        return 'success';
      case ACCOUNT_STATE.DELETED:
        return 'danger';
      default:
        return 'warning';
    }
  };

  const getPaymentStateBadgeVariant = (state: PaymentState) => {
    switch (state) {
      case PAYMENT_STATE.SUCCEEDED:
        return 'success';
      case PAYMENT_STATE.PROCESSING:
        return 'primary';
      case PAYMENT_STATE.FAILED:
        return 'danger';
      case PAYMENT_STATE.READY:
      default:
        return 'neutral';
    }
  };

  const getEnrollmentStateBadgeVariant = (state: EnrollmentState) => {
    switch (state) {
      case ENROLLMENT_STATE.IN_PROGRESS:
        return 'success';
      case ENROLLMENT_STATE.COMPLETED:
        return 'primary';
      default:
        return 'neutral';
    }
  };

  const isSelf = currentUser && account && Number(currentUser.id) === account.id;

  return (
    <DetailPageLayout
      title="사용자 상세 정보"
      subtitle={account ? `ID: ${account.id} | ${account.email}` : undefined}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error instanceof Error ? error.message : undefined}
      onRetry={refetch}
      actions={
        account &&
        currentUser &&
        !isSelf &&
        canModifyAccount(currentUser.kind, account.kind) && (
          <Button onClick={handleResetPasswordClick} variant="warning" leftIcon={<HiRefresh />}>
            비밀번호 초기화
          </Button>
        )
      }
      secondaryActions={
        account &&
        currentUser &&
        account.state !== ACCOUNT_STATE.DELETED &&
        !isSelf &&
        canModifyAccount(currentUser.kind, account.kind) && (
          <Button onClick={handleDeleteClick} variant="danger" leftIcon={<HiTrash />}>
            탈퇴 처리
          </Button>
        )
      }>
      {account && (
        <>
          {/* 비밀번호 초기화 확인 모달 */}
          <ConfirmModal
            isOpen={showResetPasswordModal}
            onClose={() => setShowResetPasswordModal(false)}
            type="warning"
            title="비밀번호 초기화"
            message="정말로 이 사용자의 비밀번호를 초기화하시겠습니까?"
            infoBox={{
              content: (
                <>
                  <strong>안내:</strong> 비밀번호 초기화 시 새로운 임시 비밀번호가 자동으로 생성되어{' '}
                  <strong>{account.email}</strong>로 전송됩니다.
                </>
              ),
            }}
            buttons={[
              {
                label: '취소',
                onClick: () => setShowResetPasswordModal(false),
                variant: 'secondary',
                disabled: isResettingPassword,
              },
              {
                label: '초기화',
                onClick: handleConfirmResetPassword,
                variant: 'danger',
                isLoading: isResettingPassword,
              },
            ]}
          />

          {/* 결과 모달 */}
          <ConfirmModal
            isOpen={showResultModal !== null}
            onClose={() => setShowResultModal(null)}
            type={showResultModal?.type || 'info'}
            title={
              !showResultModal
                ? ''
                : showResultModal.type === 'success'
                  ? showResultModal.message.includes('유형')
                    ? '유형 변경 완료'
                    : showResultModal.message.includes('탈퇴')
                      ? '탈퇴 처리 완료'
                      : '비밀번호 초기화 완료'
                  : showResultModal.message.includes('유형')
                    ? '유형 변경 실패'
                    : showResultModal.message.includes('탈퇴')
                      ? '탈퇴 처리 실패'
                      : '비밀번호 초기화 실패'
            }
            message={showResultModal?.message || ''}
            buttons={[
              {
                label: '확인',
                onClick: () => setShowResultModal(null),
                variant: 'primary',
              },
            ]}
          />

          {/* 유형 변경 모달 */}
          <ConfirmModal
            isOpen={showChangeKindModal}
            onClose={() => setShowChangeKindModal(false)}
            type="info"
            title="사용자 유형 변경"
            message={
              <div className="space-y-4">
                <p className="text-base text-slate-600">사용자의 유형을 변경하시겠습니까?</p>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">새 유형 선택</label>
                  <select
                    value={selectedKind}
                    onChange={(e) => setSelectedKind(e.target.value as AccountKind)}
                    disabled={isChangingKind}
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-base text-slate-800 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100">
                    {ACCOUNT_KIND_OPTIONS_ORDER.filter((kind) => {
                      if (currentUser?.kind) {
                        return canChangeToKind(currentUser.kind, kind);
                      }
                      return true;
                    }).map((kind) => (
                      <option key={kind} value={kind}>
                        {ROLE_LABELS[kind]} ({kind})
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-slate-500">
                    현재 유형: <strong>{account?.kind}</strong> → 변경 후: <strong>{selectedKind}</strong>
                  </p>
                </div>
              </div>
            }
            buttons={[
              {
                label: '취소',
                onClick: () => setShowChangeKindModal(false),
                variant: 'secondary',
                disabled: isChangingKind,
              },
              {
                label: '변경',
                onClick: handleConfirmChangeKind,
                variant: 'primary',
                isLoading: isChangingKind,
              },
            ]}
          />

          {/* 탈퇴 처리 확인 모달 */}
          <ConfirmModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            type="danger"
            title="사용자 탈퇴 처리"
            message="정말로 이 사용자를 탈퇴 처리하시겠습니까?"
            infoBox={{
              content: (
                <>
                  <strong>주의:</strong> 탈퇴 처리 시 사용자의 개인정보는 마스킹 처리되며, 상태가{' '}
                  <strong>DELETED</strong>로 변경됩니다. 이 작업은 되돌릴 수 없습니다.
                </>
              ),
            }}
            buttons={[
              {
                label: '취소',
                onClick: () => setShowDeleteModal(false),
                variant: 'secondary',
                disabled: isDeleting,
              },
              {
                label: '탈퇴 처리',
                onClick: handleConfirmDelete,
                variant: 'danger',
                isLoading: isDeleting,
              },
            ]}
          />

          {/* 기본 정보 */}
          <Card title="기본 정보">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InfoItem label="이름" value={account.name} />
              <InfoItem label="이메일" value={account.email} />
              <InfoItem label="전화번호" value={account.phone} />
              <InfoItem
                label="상태"
                value={<Badge variant={getStateBadgeVariant(account.state)}>{account.state}</Badge>}
              />
              <InfoItem
                label="유형"
                value={
                  <div className="flex items-center gap-2">
                    <Badge variant={getKindBadgeVariant(account.kind)}>
                      {ROLE_LABELS[account.kind] ?? account.kind}
                    </Badge>
                    {currentUser && canModifyAccount(currentUser.kind, account.kind) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleChangeKindClick}
                        leftIcon={<HiPencil />}
                        className="h-6 px-2 text-xs">
                        변경
                      </Button>
                    )}
                  </div>
                }
              />
              <InfoItem label="성별" value={account.gender} />
              <InfoItem label="생년월일" value={account.dob} />
              <InfoItem label="소속 기관 ID" value={account.institutionId?.toString()} />
            </div>
          </Card>

          {/* 인증 정보 */}
          <Card title="인증 정보">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InfoItem label="이메일 인증일" value={formatDateTimeKoreanLocale(account.emailCertifiedAt)} />
              <InfoItem label="전화번호 인증일" value={formatDateTimeKoreanLocale(account.phoneCertifiedAt)} />
              <InfoItem
                label="이메일 마케팅 동의일"
                value={formatDateTimeKoreanLocale(account.emailMarketingAgreedAt)}
              />
              <InfoItem label="전화 마케팅 동의일" value={formatDateTimeKoreanLocale(account.phoneMarketingAgreedAt)} />
            </div>
          </Card>

          {/* 계정 관리 */}
          <Card title="계정 관리">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InfoItem label="생성일" value={formatDateTimeKoreanLocale(account.createdAt)} />
              <InfoItem label="수정일" value={formatDateTimeKoreanLocale(account.updatedAt)} />
              <InfoItem label="탈퇴 코드" value={account.withdrawalCode} />
              <InfoItem label="탈퇴 사유" value={account.withdrawalReason} />
            </div>
          </Card>

          {/* 소셜 계정 */}
          <Card title={`소셜 계정 (${account.socialAccounts.length})`}>
            {account.socialAccounts.length > 0 ? (
              <div className="space-y-3">
                {account.socialAccounts.map((social, index) => (
                  <div key={`social-${social.id}-${index}`} className="rounded-lg border border-slate-200 p-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <InfoItem label="제공자" value={social.provider} />
                      <InfoItem label="제공자 계정 ID" value={social.providerAccountId} />
                      <InfoItem label="상태" value={social.state} />
                      <InfoItem label="생성일" value={formatDateTimeKoreanLocale(social.createdAt)} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">연동된 소셜 계정이 없습니다.</p>
            )}
          </Card>

          {/* 수강 정보 */}
          <Card title={`수강 정보 (${account.enrollmentMeta?.total || account.enrollments.length})`}>
            {account.enrollments.length > 0 || additionalEnrollments.length > 0 ? (
              <>
                <div className="space-y-4">
                  {[...account.enrollments, ...additionalEnrollments].map((enrollment, index) => {
                    const hasCourse = enrollment.course && enrollment.course.title;

                    return (
                      <div
                        key={`enrollment-${enrollment.id}-${index}`}
                        onClick={() => navigate(`/dashboard/user/${accountId}/enrollment/${enrollment.id}`)}
                        className="cursor-pointer rounded-lg border border-slate-200 p-5 transition-all hover:border-indigo-300 hover:shadow-md">
                        <div className="mb-3">
                          <h4 className="mb-1 text-lg font-semibold text-slate-800">
                            {hasCourse ? enrollment.course?.title : `코스 정보 없음`}
                          </h4>
                        </div>

                        <div className="flex gap-6 border-t border-slate-100 pt-3">
                          <InfoItem
                            label="상태"
                            value={
                              <Badge variant={getEnrollmentStateBadgeVariant(enrollment.state)}>
                                {enrollment.state}
                              </Badge>
                            }
                          />
                          <InfoItem label="등록일" value={formatDateTimeKoreanLocale(enrollment.enrolledAt)} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {account.enrollmentMeta && currentPage < account.enrollmentMeta.totalPages && (
                  <div className="mt-6 text-center">
                    <Button
                      onClick={handleLoadMoreEnrollments}
                      disabled={isLoadingMore}
                      variant="secondary"
                      isLoading={isLoadingMore}>
                      더 보기 ({currentPage}/{account.enrollmentMeta.totalPages})
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">수강 중인 코스가 없습니다.</p>
            )}
          </Card>

          {/* 결제 내역 */}
          <Card title={`결제 내역 (${paymentsData?.meta?.total || paymentsData?.data?.length || 0})`}>
            {(paymentsData?.data && paymentsData.data.length > 0) || additionalPayments.length > 0 ? (
              <>
                <div className="space-y-4">
                  {[...(paymentsData?.data || []), ...additionalPayments].map((payment) => (
                    <div
                      key={payment.id}
                      onClick={() => navigate(`/dashboard/payments/${payment.id}`)}
                      className="cursor-pointer rounded-lg border border-slate-200 p-5 transition-all hover:border-indigo-300 hover:shadow-md">
                      <div className="mb-3 flex items-center justify-between">
                        <div>
                          <h4 className="mb-1 text-lg font-semibold text-slate-800">주문서 #{payment.orderSheetId}</h4>
                          <p className="text-sm text-slate-500">{formatDateTimeKoreanLocale(payment.createdAt)}</p>
                        </div>
                        <Badge variant={getPaymentStateBadgeVariant(payment.state)}>
                          {PAYMENT_STATE_LABELS[payment.state]}
                        </Badge>
                      </div>

                      <div className="flex gap-6 border-t border-slate-100 pt-3">
                        <InfoItem label="결제 수단" value={payment.method} />
                        <InfoItem label="결제 금액" value={`${payment.amount.toLocaleString()}원`} />
                      </div>
                    </div>
                  ))}
                </div>

                {paymentsData?.meta && currentPaymentPage < paymentsData.meta.totalPages && (
                  <div className="mt-6 text-center">
                    <Button
                      onClick={handleLoadMorePayments}
                      disabled={isLoadingMorePayments}
                      variant="secondary"
                      isLoading={isLoadingMorePayments}>
                      더 보기 ({currentPaymentPage}/{paymentsData.meta.totalPages})
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">결제 내역이 없습니다.</p>
            )}
          </Card>
        </>
      )}
    </DetailPageLayout>
  );
};

export default UserDetail;
